/**
 * GDPR Article 8 — Children's consent.
 *
 * In the EU, the default age at which a child can consent to information
 * society services on their own is 16 (Art. 8(1)). Member states may lower
 * it to 13. For Austria, the age is **14** (§ 4(4) DSG). For processing of
 * children's data in the school context (special categories, non-ISS),
 * parental consent is required for minors regardless of digital-consent age.
 *
 * We apply **16** as the threshold because:
 *  - Safer default (matches GDPR default)
 *  - Applies uniformly to EU-wide deployment
 *  - The system processes Art. 9 health data, which always requires heightened
 *    protection and explicit parental consent for minors
 */
export const GDPR_MINOR_AGE_THRESHOLD = 16;

/**
 * Compute age in full years from birthDate to a reference date (default: now).
 * Returns null if birthDate is invalid/missing.
 */
export function computeAge(birthDate: Date | string | undefined | null, referenceDate: Date = new Date()): number | null {
  if (!birthDate) return null;
  const bd = birthDate instanceof Date ? birthDate : new Date(birthDate);
  if (Number.isNaN(bd.getTime())) return null;

  let age = referenceDate.getFullYear() - bd.getFullYear();
  const monthDiff = referenceDate.getMonth() - bd.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && referenceDate.getDate() < bd.getDate())) {
    age--;
  }
  return age;
}

/**
 * Returns true if the child is a minor under GDPR Art. 8 (<16 by default).
 * Returns false if child is adult. Returns null if age cannot be determined
 * (caller must decide: safer default is to treat unknown as minor).
 */
export function isMinorForConsent(birthDate: Date | string | undefined | null): boolean | null {
  const age = computeAge(birthDate);
  if (age === null) return null;
  return age < GDPR_MINOR_AGE_THRESHOLD;
}

/** Format birthDate as ISO date string (YYYY-MM-DD) for consent audit snapshot. */
export function toBirthDateString(birthDate: Date | string | undefined | null): string | undefined {
  if (!birthDate) return undefined;
  const bd = birthDate instanceof Date ? birthDate : new Date(birthDate);
  if (Number.isNaN(bd.getTime())) return undefined;
  return bd.toISOString().slice(0, 10);
}
